import { Select, SelectOption, SelectOptionGroup, Text } from "@amzn/meridian";
import React from "react";
import { HIERARCHY_SEPARATOR, OTHER } from "src/constants";
import { AuditField, AuditResult, setFieldConfirmedAnswer } from "src/store/auditSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import { isFieldMultiSelected } from "src/components/pages/audit/helpers";

interface ChildFieldProps {
  auditField: AuditField;
  fieldIndex: number;
  value: string | string[];
  hasInadequateInfo: boolean;
  hasOtherOption: boolean;
  searchQuery: string;
  shouldShowError: boolean;
  setSearchQuery: (val: string) => void;
}

const ChildField = ({
  auditField,
  fieldIndex,
  value,
  searchQuery,
  setSearchQuery,
}: ChildFieldProps) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const { selectedAuditData } = useAppSelector((state) => state.auditSlice);

  const isChildMultiSelect = isFieldMultiSelected(auditField, selectedModel);

  const hierarchyRule = selectedModel?.hierarchy?.find(
    (h) => h.childFieldName === auditField.fieldName,
  );

  const parentField = selectedAuditData.auditFields.find(
    (field) => field.fieldName === hierarchyRule?.parentFieldName,
  );

  if (
    !parentField?.confirmedAnswer ||
    parentField?.confirmedAnswer === "" ||
    (Array.isArray(parentField.confirmedAnswer) && parentField.confirmedAnswer.length === 0)
  ) {
    return <Text type={"b100"}>Please select {hierarchyRule?.parentFieldName}</Text>;
  }

  const getChildOptions = (parentValue: string): string[] => {
    if (!hierarchyRule) return [];

    // Extract the actual parent value if it contains hierarchy separator
    const actualParentValue = parentValue.includes(HIERARCHY_SEPARATOR)
      ? parentValue.split(HIERARCHY_SEPARATOR).pop() || parentValue
      : parentValue;

    return (hierarchyRule.options[actualParentValue] || []).map(
      (childValue) => `${actualParentValue}${HIERARCHY_SEPARATOR}${childValue}`,
    );
  };

  const getOptionLabel = (option: string): string => {
    if (option.includes(HIERARCHY_SEPARATOR)) {
      const parts = option.split(HIERARCHY_SEPARATOR);
      return parts[parts.length - 1];
    }
    return option;
  };

  const parentAnswers = Array.isArray(parentField.confirmedAnswer)
    ? parentField.confirmedAnswer
    : typeof parentField.confirmedAnswer === "string" && parentField.confirmedAnswer.includes("|")
      ? parentField.confirmedAnswer.split("|")
      : [parentField.confirmedAnswer];

  // Filter out only "Other" from parent answers (No Object Involved and Inadequate Information have mappings)
  const validParentAnswers = parentAnswers.filter((answer: string) => {
    const label = getOptionLabel(answer);
    return !compareStringsIgnoreCase(label, OTHER);
  });

  // Combine child options: always filter by selected parents
  const allChildOptionsRaw: string[] =
    validParentAnswers.length > 0
      ? validParentAnswers.reduce((acc: string[], parentValue: string) => {
          return acc.concat(getChildOptions(parentValue));
        }, [])
      : [];

  // Deduplicate by label (the part after ---)
  const seenLabels = new Set<string>();
  const allChildOptions: string[] = allChildOptionsRaw.filter((option: string) => {
    const label = getOptionLabel(option).toLowerCase();
    if (seenLabels.has(label)) {
      return false;
    }
    seenLabels.add(label);
    return true;
  });

  // Format value for multi-select
  let formattedValue: string | string[] = value;
  if (isChildMultiSelect) {
    if (typeof value === "string") {
      formattedValue = value.includes("|") ? value.split("|") : value ? [value] : [];
    } else if (Array.isArray(value)) {
      formattedValue = value;
    } else {
      formattedValue = [];
    }
    // Always filter values to match available options
    if (Array.isArray(formattedValue)) {
      const availableLabels = new Set(
        allChildOptions.map((opt: string) => getOptionLabel(opt).toLowerCase()),
      );
      formattedValue = formattedValue.filter((val: string) => {
        const valLabel = getOptionLabel(val).toLowerCase();
        return availableLabels.has(valLabel);
      });
      formattedValue = formattedValue.map((val: string) => {
        const valLabel = getOptionLabel(val);
        return (
          allChildOptions.find((opt: string) =>
            compareStringsIgnoreCase(getOptionLabel(opt), valLabel),
          ) || val
        );
      });
    }
  } else {
    formattedValue =
      typeof value === "string" ? value : Array.isArray(value) && value.length > 0 ? value[0] : "";
  }

  return (
    <Select
      value={formattedValue}
      onChange={(answer) => {
        // Strip parent prefix from child values before saving
        let processedAnswer: string | string[];
        if (Array.isArray(answer)) {
          processedAnswer = answer
            .filter((option: string) => option)
            .map((option: string) => getOptionLabel(option));
        } else {
          processedAnswer = getOptionLabel(answer);
        }

        dispatch(
          setFieldConfirmedAnswer({
            fieldIndex: fieldIndex,
            confirmedAnswer: processedAnswer,
            alias: alias,
            auditResult: AuditResult.NEITHER,
          }),
        );
      }}
      placeholder={"Choose"}
      size="medium"
      searchQuery={searchQuery}
      onSearch={setSearchQuery}
    >
      <SelectOptionGroup label={"Options"}>
        {allChildOptions.map((option: string) => (
          <SelectOption key={option} value={option} label={getOptionLabel(option)} />
        ))}
      </SelectOptionGroup>
    </Select>
  );
};

export default ChildField;
